// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the legend can be closed and brought back (owner, 2026-10-11).
 * A closed legend is parked at the foot of the Tools panel's View tab,
 * and goes back to the globe the way the pin graph comes out of the
 * Data tab (pinGraphWindow.ts), with a flight between the two places:
 *
 *   on the globe                       Tools → View
 *   ┌──────────────────── ✕ ┐           LEGEND
 *   │ ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬ │  ──✕──▶   ┌ - - - - - - - - - - - - - ⇱ ┐
 *   │ 0    5    10    15    │  ◀──⇱──   ╎ ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬   ╎
 *   └───────────────────────┘           └ - - - - - - - - - - - - - - ┘
 *
 *   - its ✕, or a drag of it onto the Tools panel or the Tools button,
 *     parks it here;
 *   - ⇱, or a drag of it out of here onto the globe, puts it back: where
 *     it was let go, or else where it last sat (legendPlacement.ts).
 *
 * One switch for every globe: in a two- or four-globe layout they are
 * parked and brought back together, each flying to its own place. The
 * switch is the old Legend toggle of Tools → More (`legendVisible`), so
 * a parked legend stays parked on the next visit. A finger drags it out
 * by ⇱ only, as it does the graph: over the legend itself it scrolls the
 * panel.
 */

import { t, tAttr, tHtml } from '../i18n'

export interface LegendDockDeps {
  /** The legends are parked here rather than on the globes. */
  parked(): boolean
  /** Park them (true) or put them back on the globes (false); the app redraws the legends at once. */
  setParked(parked: boolean): void
  /** Fresh legends to show here while parked, one per globe that has one. */
  parkedLegends(): Array<{ slot: number; el: HTMLElement }>
  /** The dataset on the globe has a legend at all (values or a picture). */
  hasLegend(): boolean
  /** The legends on the globes now. */
  floatingLegends(): Array<{ slot: number; el: HTMLElement }>
  /** A legend let go over the globe at `rect` (client px) stays there. */
  placeAt(slot: number, rect: DOMRect): void
  /** Show Tools → View (a legend dropped on the open panel lands in it). */
  showView(): void
  /** On a phone the panel covers the globe: it gets out of the way. */
  stepAside?(): void
  /** An immersive session is on: the page is not drawn, so nothing flies. */
  immersive?(): boolean
  announce?(message: string): void
}

export interface LegendDockHandle {
  /** Redraw the parked legends (a palette, a dataset, a layout changed). */
  refresh(): void
  /** Park the legends, each flying from where it is on its globe. */
  park(): void
  /** Bring them back, each flying from here (or from `origin`) to its globe. */
  unpark(origin?: HTMLElement | null): void
  /** Where a legend carried on the globe can be let go to park it. */
  dropTargets(): HTMLElement[]
  /** A legend carried on the globe is over a drop target, or none. */
  hover(target: HTMLElement | null): void
  /** It was let go over `target`: parked, in the View tab if that was the open panel. */
  drop(target: HTMLElement): void
}

const FLY_MS = 420
const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)'
/** How far a press moves before it lifts the legend out. */
const LIFT_PX = 6

const ICON_POP_OUT = '<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><path d="M9 3h4v4M13 3L7.5 8.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M11 9.5V13H3V5h3.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'

function reducedMotion(): boolean {
  return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

function shown(el: Element | null | undefined): el is HTMLElement {
  return !!el && el.getClientRects().length > 0 && el.getBoundingClientRect().width > 0
}

function inside(r: DOMRect, x: number, y: number): boolean {
  return r.width > 0 && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom
}

/**
 * A copy of `source` standing at `from` on top of everything, for a
 * flight. Not under the globe's panel, so the panel's own placing rules
 * (its corner, its lift, its step aside) are cleared off it.
 */
export function legendGhost(source: HTMLElement, from: DOMRect, layer: HTMLElement = document.body): HTMLElement {
  const ghost = source.cloneNode(true) as HTMLElement
  ghost.removeAttribute('id')
  for (const el of ghost.querySelectorAll('[id]')) el.removeAttribute('id')
  ghost.querySelector('.legend-close')?.remove()
  ghost.classList.add('legend-ghost')
  ghost.classList.remove('hidden', 'legend-dragging', 'legend-resizing', 'legend-over-dock')
  ghost.setAttribute('aria-hidden', 'true')
  ghost.removeAttribute('tabindex')
  Object.assign(ghost.style, {
    position: 'fixed',
    left: `${from.left}px`,
    top: `${from.top}px`,
    right: 'auto',
    bottom: 'auto',
    insetInlineEnd: 'auto',
    width: `${from.width}px`,
    height: `${from.height}px`,
    inlineSize: `${from.width}px`,
    blockSize: `${from.height}px`,
    margin: '0',
    translate: 'none',
    transform: 'none',
    transformOrigin: '0 0',
    transition: 'none',
    boxSizing: 'border-box',
    pointerEvents: 'none',
    zIndex: '2000',
  })
  layer.appendChild(ghost)
  return ghost
}

/**
 * Fly a ghost from where it stands (`from`) to `to`, stretching to its
 * size, and take it away. A small target (a button) swallows it: it
 * fades on the way. At once under reduced motion.
 */
export function flyGhost(ghost: HTMLElement, from: DOMRect, to: DOMRect, opts: { fadeOut?: boolean; fadeIn?: boolean } = {}): Promise<void> {
  const done = () => ghost.remove()
  if (reducedMotion() || typeof ghost.animate !== 'function') {
    done()
    return Promise.resolve()
  }
  const sx = Math.max(0.05, to.width / Math.max(1, from.width))
  const sy = Math.max(0.05, to.height / Math.max(1, from.height))
  const anim = ghost.animate([
    { transform: 'translate(0, 0) scale(1, 1)', opacity: opts.fadeIn ? 0.2 : 1 },
    { transform: `translate(${to.left - from.left}px, ${to.top - from.top}px) scale(${sx}, ${sy})`, opacity: opts.fadeOut ? 0 : 1 },
  ], { duration: FLY_MS, easing: EASE, fill: 'forwards' })
  return anim.finished.then(done, done)
}

/** Flights to a small target fade into it. */
function small(r: DOMRect): boolean {
  return r.width < 80 || r.height < 40
}

export function mountLegendDock(host: HTMLElement, deps: LegendDockDeps): LegendDockHandle {
  host.classList.add('legend-dock')
  host.innerHTML = `
    <h4 class="tools-menu-section-title">${tHtml('legend.dock.title')}</h4>
    <div class="legend-dock-slot" id="legend-dock-slot">
      <div class="legend-dock-items" id="legend-dock-items"></div>
      <div class="legend-dock-away" id="legend-dock-away">
        <span class="legend-dock-away-text" id="legend-dock-away-text"></span>
        <button type="button" class="tools-data-action" id="legend-dock-park">${tHtml('legend.dock.park')}</button>
      </div>
    </div>`
  const slotBox = host.querySelector<HTMLElement>('#legend-dock-slot')!
  const items = host.querySelector<HTMLElement>('#legend-dock-items')!
  const away = host.querySelector<HTMLElement>('#legend-dock-away')!
  const awayText = host.querySelector<HTMLElement>('#legend-dock-away-text')!
  const parkBtn = host.querySelector<HTMLButtonElement>('#legend-dock-park')!

  /** A legend on the globe is carried over the panel or the button. */
  let dropping = false
  /** A flight is landing here: the items wait unseen until it does. */
  let landing = false
  /** A parked legend is being carried out. */
  let lifting: number | null = null
  let lit: HTMLElement | null = null

  const popover = () => document.getElementById('tools-menu-popover')
  const toggle = () => document.getElementById('tools-menu-toggle')
  const panelOpen = () => {
    const p = popover()
    return !!p && !p.classList.contains('hidden') && shown(p)
  }
  const flying = () => !deps.immersive?.() && !reducedMotion()

  function render(): void {
    const parked = deps.parked()
    const legends = parked ? deps.parkedLegends() : []
    const has = parked ? legends.length > 0 : deps.hasLegend()
    host.hidden = !has
    if (!has) {
      items.textContent = ''
      return
    }
    // Keep the focus on ⇱ across a redraw (a palette change redraws it).
    const focused = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>('.legend-dock-pop')
    const focusSlot = focused && items.contains(focused) ? focused.dataset.slot : null
    items.textContent = ''
    for (const { slot, el } of legends) {
      const item = document.createElement('div')
      item.className = 'legend-dock-item'
      item.dataset.slot = String(slot)
      item.title = t('legend.dock.parkedNote')
      if (legends.length > 1) {
        const label = document.createElement('span')
        label.className = 'legend-dock-globe'
        label.textContent = t('legend.dock.globe', { n: slot + 1 })
        item.appendChild(label)
      }
      const box = document.createElement('div')
      box.className = 'legend-dock-legend'
      box.appendChild(el)
      item.appendChild(box)
      const pop = document.createElement('button')
      pop.type = 'button'
      pop.className = 'legend-dock-pop'
      pop.dataset.slot = String(slot)
      pop.innerHTML = ICON_POP_OUT
      pop.setAttribute('aria-label', tAttr('legend.dock.popOut'))
      pop.title = t('legend.dock.popOut')
      item.appendChild(pop)
      if (lifting === slot) item.classList.add('lifting')
      items.appendChild(item)
      if (focusSlot === String(slot)) pop.focus({ preventScroll: true })
    }
    items.hidden = !parked
    items.classList.toggle('landing', landing)
    away.hidden = parked
    slotBox.classList.toggle('is-parked', parked)
    away.classList.toggle('drop-here', dropping)
    awayText.textContent = t(dropping ? 'legend.dock.dropHere' : 'legend.dock.away')
    parkBtn.hidden = dropping
  }

  /** Where a flight into this section ends for globe `slot`: its item, else the tab, else the Tools button. */
  function landingRect(slot: number): DOMRect | null {
    const item = items.querySelector<HTMLElement>(`.legend-dock-item[data-slot="${slot}"] .legend-dock-legend`)
    if (panelOpen() && shown(item)) return item.getBoundingClientRect()
    const tab = document.getElementById('tools-menu-tab-view')
    if (panelOpen() && shown(tab)) return tab.getBoundingClientRect()
    const btn = toggle()
    return shown(btn) ? btn.getBoundingClientRect() : null
  }

  function glow(): void {
    for (const item of items.querySelectorAll<HTMLElement>('.legend-dock-item')) {
      item.classList.remove('landed')
      void item.offsetWidth
      item.classList.add('landed')
      setTimeout(() => item.classList.remove('landed'), 900)
    }
  }

  function park(): void {
    if (deps.parked()) return
    const leaving = flying()
      ? deps.floatingLegends().filter(({ el }) => shown(el)).map(({ slot, el }) => {
        const rect = el.getBoundingClientRect()
        return { slot, rect, ghost: legendGhost(el, rect) }
      })
      : []
    landing = leaving.length > 0
    deps.setParked(true)
    render()
    deps.announce?.(t('legend.dock.parkedAnnounce'))
    if (!leaving.length) return
    void Promise.all(leaving.map(({ slot, rect, ghost }) => {
      const to = landingRect(slot)
      return to ? flyGhost(ghost, rect, to, { fadeOut: small(to) }) : (ghost.remove(), Promise.resolve())
    })).then(() => {
      landing = false
      items.classList.remove('landing')
      glow()
    })
  }

  /**
   * Back to the globes. Each flies from its place here (from `origin`,
   * or the Tools button, when this section is out of sight); `carried`
   * is a ghost already in the air, let go over the globe.
   */
  function unpark(origin?: HTMLElement | null, carried?: { slot: number; ghost: HTMLElement; rect: DOMRect }): void {
    if (!deps.parked()) {
      carried?.ghost.remove()
      return
    }
    const fly = flying()
    const starts = new Map<number, { rect: DOMRect; ghost: HTMLElement }>()
    if (carried) starts.set(carried.slot, { rect: carried.rect, ghost: carried.ghost })
    if (fly) {
      const fallback = [origin, toggle()].find((el) => shown(el)) ?? null
      for (const item of items.querySelectorAll<HTMLElement>('.legend-dock-item')) {
        const slot = Number(item.dataset.slot)
        if (starts.has(slot)) continue
        const legend = item.querySelector<HTMLElement>('.legend-dock-legend > *')
        const here = panelOpen() && shown(legend) ? legend : null
        const from = (here ?? fallback)?.getBoundingClientRect()
        if (!legend || !from) continue
        starts.set(slot, { rect: from, ghost: legendGhost(legend, from) })
        if (!here) starts.get(slot)!.ghost.style.opacity = '0'
      }
    }
    lifting = null
    deps.setParked(false)
    render()
    deps.announce?.(t('legend.dock.backAnnounce'))
    const landed = new Map(deps.floatingLegends().map(({ slot, el }) => [slot, el]))
    for (const [slot, { rect, ghost }] of starts) {
      const el = landed.get(slot)
      if (!el || !fly) {
        ghost.remove()
        continue
      }
      el.classList.add('legend-arriving')
      const fromButton = ghost.style.opacity === '0'
      ghost.style.opacity = ''
      void flyGhost(ghost, rect, el.getBoundingClientRect(), { fadeIn: fromButton }).then(() => el.classList.remove('legend-arriving'))
    }
  }

  /** A press on a parked legend that moves lifts it out; let go over the globe, it lands there. */
  function onItemPointerDown(e: PointerEvent): void {
    if (e.button > 0 || lifting !== null || deps.immersive?.()) return
    const item = (e.target as HTMLElement).closest<HTMLElement>('.legend-dock-item')
    if (!item) return
    const onPop = !!(e.target as HTMLElement).closest('.legend-dock-pop')
    if (e.pointerType === 'touch' && !onPop) return
    if (!onPop) e.preventDefault()
    const slot = Number(item.dataset.slot)
    const legend = item.querySelector<HTMLElement>('.legend-dock-legend > *')
    if (!legend) return
    let carry: { ghost: HTMLElement; rect: DOMRect; grabX: number; grabY: number } | null = null
    let last = { x: e.clientX, y: e.clientY }
    const overPanel = (x: number, y: number) => {
      const p = popover()
      const b = toggle()
      return (panelOpen() && inside(p!.getBoundingClientRect(), x, y)) || (shown(b) && inside(b.getBoundingClientRect(), x, y))
    }
    const move = (m: PointerEvent) => {
      if (m.pointerId !== e.pointerId) return
      last = { x: m.clientX, y: m.clientY }
      if (!carry) {
        if (Math.hypot(m.clientX - e.clientX, m.clientY - e.clientY) <= LIFT_PX) return
        const rect = legend.getBoundingClientRect()
        carry = { ghost: legendGhost(legend, rect), rect, grabX: e.clientX - rect.left, grabY: e.clientY - rect.top }
        carry.ghost.classList.add('legend-ghost-carried')
        lifting = slot
        item.classList.add('lifting')
        skipClick = onPop
        // On a phone the panel is most of the screen: it gets out of the way of the drop.
        deps.stepAside?.()
      }
      m.preventDefault()
      carry.ghost.style.left = `${m.clientX - carry.grabX}px`
      carry.ghost.style.top = `${m.clientY - carry.grabY}px`
      carry.ghost.classList.toggle('legend-over-dock', overPanel(m.clientX, m.clientY))
    }
    const end = (u: PointerEvent) => {
      if (u.pointerId !== e.pointerId) return
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', end)
      document.removeEventListener('pointercancel', end)
      if (!carry) return
      const { ghost } = carry
      const at = ghost.getBoundingClientRect()
      ghost.classList.remove('legend-ghost-carried', 'legend-over-dock')
      if (u.type === 'pointerup' && !overPanel(last.x, last.y)) {
        deps.placeAt(slot, at)
        unpark(null, { slot, ghost, rect: at })
        return
      }
      // Let go over the panel, or cancelled: back into its place.
      lifting = null
      const to = landingRect(slot)
      const back = () => items.querySelector(`.legend-dock-item[data-slot="${slot}"]`)?.classList.remove('lifting')
      if (to) void flyGhost(ghost, at, to, { fadeOut: small(to) }).then(back)
      else {
        ghost.remove()
        back()
      }
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', end)
    document.addEventListener('pointercancel', end)
  }

  /** Set when a press on ⇱ became a drag, so the click that follows does not unpark twice. */
  let skipClick = false

  items.addEventListener('pointerdown', onItemPointerDown)
  items.addEventListener('click', (ev) => {
    const pop = (ev.target as HTMLElement).closest('.legend-dock-pop')
    ev.stopPropagation()
    if (!pop) return
    if (skipClick) {
      skipClick = false
      return
    }
    // On a phone the panel covers the globe; it steps aside first, and the legends fly from the Tools button.
    deps.stepAside?.()
    unpark(toggle())
  })
  parkBtn.addEventListener('click', (ev) => {
    ev.stopPropagation()
    park()
    host.querySelector<HTMLElement>('.legend-dock-pop')?.focus({ preventScroll: true })
  })

  render()

  function hover(target: HTMLElement | null): void {
    if (target === lit) return
    lit?.classList.remove('legend-dock-target')
    target?.classList.add('legend-dock-target')
    lit = target
    const next = !!target
    if (next !== dropping) {
      dropping = next
      render()
    }
  }

  return {
    refresh: render,
    park,
    unpark: (origin) => unpark(origin),
    dropTargets() {
      const p = popover()
      return [panelOpen() ? p : null, toggle()].filter((el): el is HTMLElement => shown(el))
    },
    hover,
    drop(target) {
      hover(null)
      if (target === popover()) deps.showView()
      park()
    },
  }
}
